import { useState, useEffect } from 'react';
import { Store, Search, Plus, X, Tag, Check, MapPin, Users } from 'lucide-react';
import api from '../lib/api';
import { BottomSheet } from './Overlay';
import VenueInviteSheet from './VenueInviteSheet';

/**
 * VenuePartnerPicker — surfaces partner venues during plan creation.
 *
 * Invariants:
 *  - A venue offer is ONLY shown when the business is officially registered
 *    AND the offer is active (search endpoint filters status=active).
 *  - When no partner matches, only the "Invite this venue" CTA is shown.
 *  - Suggestions are category-aware (Fitness → Gym/Yoga, not cafés). The
 *    "Search all partners" toggle bypasses the filter.
 *
 * Props:
 *  - city (string) — UAE emirate used to scope search
 *  - planCategory (string) — plan category used to filter venue types
 *  - value ({business_id, business}) — currently selected partner
 *  - venueName (string)
 *  - onChange ({business_id, business})
 */
export default function VenuePartnerPicker({ city, planCategory, value, venueName, onChange }) {
  const [open, setOpen] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const business = value?.business || null;

  const clear = () => onChange?.({ business_id: '', business: null });

  return (
    <div data-testid="venue-partner-picker">
      <label className="block text-xs font-medium text-[#6B6E73] uppercase tracking-wider mb-1.5">
        Partner with a Venue <span className="text-[#B0B3B8] normal-case">(optional)</span>
      </label>
      <p className="text-[11px] text-[#9B9EA3] mb-2 leading-relaxed">
        Only venues officially registered on ASAP with active offers appear here.
      </p>

      {business ? (
        <SelectedBusinessCard biz={business} onClear={clear} />
      ) : (
        <div className="space-y-2">
          <button
            type="button"
            data-testid="venue-picker-open"
            onClick={() => setOpen(true)}
            className="w-full flex items-center gap-3 bg-white rounded-2xl border border-dashed border-black/[0.12] p-4 active:bg-[#F1F0EA]"
          >
            <div className="w-9 h-9 rounded-2xl bg-[#B6E600]/15 text-[#3F5200] flex items-center justify-center">
              <Store size={16} />
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-bold text-[#111]">Add a Partner Venue</p>
              <p className="text-[11px] text-[#9B9EA3] mt-0.5">Unlock group offers for your attendees</p>
            </div>
            <Search size={16} className="text-[#9B9EA3]" />
          </button>
          <button
            type="button"
            data-testid="venue-picker-invite"
            onClick={() => setShowInvite(true)}
            className="w-full flex items-center gap-3 bg-white rounded-2xl border border-black/[0.06] p-3 active:bg-[#F1F0EA]"
          >
            <div className="w-8 h-8 rounded-2xl bg-[#F1F0EA] text-[#3F5200] flex items-center justify-center">
              <Plus size={14} />
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-semibold text-[#111]">Don't see your venue? Invite them</p>
              <p className="text-[11px] text-[#9B9EA3]">We'll send a branded invite to your venue contact</p>
            </div>
          </button>
        </div>
      )}

      <PickerSheet
        open={open}
        onClose={() => setOpen(false)}
        city={city}
        planCategory={planCategory}
        onPick={(biz) => { onChange?.({ business_id: biz.id, business: biz }); setOpen(false); }}
        onInvite={() => { setOpen(false); setShowInvite(true); }}
      />

      <VenueInviteSheet
        open={showInvite}
        onClose={() => setShowInvite(false)}
        defaultVenueName={venueName || ''}
      />
    </div>
  );
}

function SelectedBusinessCard({ biz, onClear }) {
  const [offers, setOffers] = useState(biz.offers || []);
  useEffect(() => {
    if (biz.offers) return;
    api.get(`/businesses/${biz.id}/offers`).then(r => setOffers(r.data || [])).catch(() => {});
  }, [biz]);

  return (
    <div data-testid="venue-partner-selected" className="bg-white rounded-2xl border border-[#B6E600]/45 p-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-2xl bg-[#B6E600]/15 text-[#3F5200] flex items-center justify-center">
          <Store size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-extrabold text-[#111] truncate">{biz.name}</p>
          <p className="text-[11px] text-[#6B6E73] truncate">
            {(biz.business_types && biz.business_types.length > 0
              ? biz.business_types.join(' · ')
              : biz.business_type || biz.category)} · {biz.city}
          </p>
        </div>
        <button
          type="button"
          data-testid="venue-partner-clear"
          onClick={onClear}
          aria-label="Remove partner venue"
          className="p-1 text-[#9B9EA3]"
        >
          <X size={16} />
        </button>
      </div>
      {offers.length > 0 && (
        <div data-testid="venue-partner-offers" className="mt-3 space-y-2">
          <p className="text-[10px] uppercase tracking-wider text-[#3F5200] font-bold">Active offers shown to attendees</p>
          {offers.map(o => <RichOfferCard key={o.id} offer={o} venueName={biz.name} />)}
        </div>
      )}
    </div>
  );
}

/** RichOfferCard — used in picker + plan details. Shows perk + min group + venue. */
export function RichOfferCard({ offer, venueName }) {
  const perk = offer.percent_off > 0
    ? `${offer.percent_off}% off`
    : (offer.type === 'complimentary' ? 'Complimentary perk'
      : offer.type === 'reserved' ? 'Reserved seating' : 'Special perk');
  return (
    <div data-testid={`offer-card-${offer.id}`} className="rounded-xl bg-[#B6E600]/10 border border-[#B6E600]/30 px-3 py-2.5">
      <p className="text-xs font-extrabold text-[#111] leading-snug">🏷️ {offer.title}</p>
      <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-[#3F5200]">
        <span className="inline-flex items-center gap-1"><Tag size={11} />{perk}</span>
        <span className="inline-flex items-center gap-1"><Users size={11} />Min {offer.min_attendees}</span>
        {venueName && <span className="inline-flex items-center gap-1"><MapPin size={11} />{venueName}</span>}
      </div>
      {(offer.valid_days?.length > 0 || offer.valid_from_time || offer.valid_to_time) && (
        <p className="mt-1 text-[10px] text-[#6B6E73]">
          {offer.valid_days?.length > 0 && <>Valid {offer.valid_days.join(', ')}</>}
          {(offer.valid_from_time || offer.valid_to_time) && (
            <> · {offer.valid_from_time || '00:00'}–{offer.valid_to_time || '23:59'}</>
          )}
        </p>
      )}
    </div>
  );
}

function PickerSheet({ open, onClose, city, planCategory, onPick, onInvite }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [searchAll, setSearchAll] = useState(false);

  useEffect(() => {
    if (!open) {
      setQ(''); setResults([]); setSearched(false); setSearchAll(false);
      return;
    }
    runSearch('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const runSearch = async (query) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (query) params.set('q', query);
      if (city) params.set('city', city);
      if (planCategory && !searchAll) params.set('plan_category', planCategory);
      const { data } = await api.get(`/businesses/search?${params.toString()}`);
      setResults(data || []);
      setSearched(true);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!open) return;
    const id = setTimeout(() => runSearch(q), 280);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, open, city, searchAll]);

  const isFiltered = planCategory && !searchAll;

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title="Find a partner venue"
      footer={
        <button
          data-testid="venue-sheet-invite-cta"
          onClick={onInvite}
          className="w-full bg-white border border-black/[0.08] text-[#111] font-bold rounded-full py-3 text-sm flex items-center justify-center gap-2"
        >
          <Plus size={14} /> Invite a venue
        </button>
      }
    >
      <div className="space-y-3" data-testid="venue-picker-sheet">
        <div className="flex items-center gap-2 bg-[#F1F0EA] rounded-xl px-3 py-2.5">
          <Search size={16} className="text-[#9B9EA3]" />
          <input
            data-testid="venue-search-input"
            type="text"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder={city ? `Search venues in ${city}` : 'Search venues'}
            className="flex-1 bg-transparent text-sm text-[#111] focus:outline-none placeholder-[#B0B3B8]"
            autoFocus
          />
        </div>

        {planCategory && (
          <div className="flex items-center justify-between gap-2 bg-[#F1F0EA] rounded-xl px-3 py-2">
            <p className="text-[11px] text-[#6B6E73] leading-tight">
              {isFiltered
                ? <>Showing venues matched to <span className="font-semibold capitalize text-[#111]">{planCategory}</span></>
                : <>Showing <span className="font-semibold text-[#111]">all partner venues</span></>}
            </p>
            <button
              type="button"
              data-testid="venue-search-all-toggle"
              onClick={() => setSearchAll(v => !v)}
              className="text-[11px] font-bold text-[#3F5200] underline-offset-2 underline"
            >
              {searchAll ? 'Filter by category' : 'Search all partners'}
            </button>
          </div>
        )}

        {loading && <p className="text-center text-xs text-[#9B9EA3] py-4">Searching…</p>}

        {!loading && results.length === 0 && searched && (
          <div data-testid="venue-search-empty" className="text-center py-6">
            <p className="text-sm text-[#6B6E73]">
              No matching partner venues{city ? ` in ${city}` : ''}
              {isFiltered ? ' for this category' : ''}
            </p>
            <p className="text-[11px] text-[#9B9EA3] mt-1">Tap "Invite a venue" below to bring them on board.</p>
          </div>
        )}

        {!loading && results.length > 0 && (
          <div className="space-y-2">
            {results.map(b => (
              <button
                key={b.id}
                type="button"
                data-testid={`venue-result-${b.id}`}
                onClick={() => onPick(b)}
                className="w-full flex items-center gap-3 bg-white rounded-2xl border border-black/[0.06] p-3 active:bg-[#F1F0EA]"
              >
                <div className="w-9 h-9 rounded-2xl bg-[#B6E600]/15 text-[#3F5200] flex items-center justify-center">
                  <Store size={16} />
                </div>
                <div className="flex-1 text-left min-w-0">
                  <p className="text-sm font-bold text-[#111] truncate">{b.name}</p>
                  <p className="text-[11px] text-[#6B6E73] truncate">
                    {(b.business_types && b.business_types.length > 0
                      ? b.business_types.join(' · ')
                      : b.business_type || b.category)} · {b.city}
                  </p>
                </div>
                <Check size={16} className="text-[#9B9EA3]" />
              </button>
            ))}
          </div>
        )}
      </div>
    </BottomSheet>
  );
}
